<template>
	<div class="p-0 min-h-screen bg-gray-100">
		<div class="bg-white py-4 px-6 border-b border-gray-200">
			<div class="mb-2">
				<router-link :to="`/template/${templateId}`" class="text-gray-500 no-underline text-sm inline-flex items-center hover:text-gray-700">
					<i class="fa fa-arrow-left mr-2"></i>
					Back to Template
				</router-link>
			</div>
			<div class="flex justify-between items-center">
				<h1 class="text-2xl font-semibold text-gray-900 m-0">Edit Template State</h1>
				<button
					class="bg-violet-600 text-white border-none rounded-md py-2 px-4 text-sm font-medium cursor-pointer hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
					@click="saveState"
					:disabled="isSaving"
				>
					{{ isSaving ? 'Saving...' : 'Save Changes' }}
				</button>
			</div>
		</div>

		<div class="p-6">
			<div v-if="isLoading" class="py-12 px-6 text-center text-gray-500">Loading template state...</div>

			<div v-else class="bg-white rounded-lg shadow-sm">
				<div class="flex justify-between items-center py-4 px-6 border-b border-gray-200">
					<h2 class="text-lg font-semibold text-gray-900 m-0">State Properties</h2>
				</div>
				<div class="p-6">
					<div class="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4 mb-6">
						<div>
							<label class="block text-sm font-medium text-gray-700 mb-1">Type</label>
							<select v-model="state.type" class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm box-border focus:border-violet-600 focus:outline-none">
								<option value="photo">photo</option>
								<option value="text">text</option>
							</select>
						</div>
						<div>
							<label class="block text-sm font-medium text-gray-700 mb-1">Priority</label>
							<input
								v-model.number="state.priority"
								type="number"
								class="w-full py-2 px-3 border border-gray-300 rounded-md text-sm box-border focus:border-violet-600 focus:outline-none"
							>
						</div>
					</div>
					<div class="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-4">
						<div class="p-3 border border-gray-200 rounded-md">
							<label class="flex items-center gap-2 cursor-pointer">
								<input
									type="checkbox"
									:checked="state.combine === 1"
									@change="state.combine = ($event.target as HTMLInputElement).checked ? 1 : 0"
									class="w-4 h-4"
								>
								<span>Combine</span>
							</label>
						</div>
					</div>
				</div>

				<div class="py-4 px-6 border-t border-gray-200 border-b border-gray-200">
					<h3 class="text-base font-semibold text-gray-900 m-0">Template Positions</h3>
				</div>
				<div v-if="isLoadingPositions" class="py-12 px-6 text-center text-gray-500">Loading positions...</div>
				<div v-else class="overflow-x-auto">
					<table class="w-full border-collapse">
						<thead>
							<tr>
								<th class="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">ID</th>
								<th class="text-left py-3 px-6 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Type</th>
								<th class="text-right py-3 px-6 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="position in positions" :key="position.id" class="border-b border-gray-200 hover:bg-gray-50">
								<td class="py-3 px-6 text-sm text-gray-700">{{ position.id }}</td>
								<td class="py-3 px-6 text-sm text-gray-700">{{ position.type }}</td>
								<td class="py-3 px-6 text-sm text-gray-700 text-right">
									<button
										class="bg-transparent border border-gray-300 rounded py-1.5 px-2.5 cursor-pointer text-gray-500 min-w-[32px] min-h-[32px] hover:bg-gray-100"
										@click="editPosition(position)"
										title="Edit"
									>
										<i class="fa fa-pencil"></i>
									</button>
								</td>
							</tr>
							<tr v-if="positions.length === 0">
								<td colspan="3" class="py-12 px-6 text-center text-gray-500">No positions found</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { TemplatePositionStateModel } from '@sosocio/creator/interfaces';
import { httpClient } from 'utils/http';
import Swal from 'sweetalert2';

interface PositionItem {
	id: number;
	type: string;
}

export default defineComponent({
	name: 'EditTemplateStatePage',
	setup() {
		const route = useRoute();
		const router = useRouter();
		const templateId = route.params.templateid as string;
		const stateId = route.params.id as string;

		const state = ref<Partial<TemplatePositionStateModel>>({});
		const positions = ref<PositionItem[]>([]);
		const isLoading = ref(false);
		const isLoadingPositions = ref(false);
		const isSaving = ref(false);

		const loadState = async () => {
			isLoading.value = true;
			try {
				const { data } = await httpClient.get<TemplatePositionStateModel>(`/api/templatestate/${stateId}`);
				state.value = data;
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to load template state',
					icon: 'error',
				});
			} finally {
				isLoading.value = false;
			}
		};

		const loadPositions = async () => {
			isLoadingPositions.value = true;
			try {
				const params = new URLSearchParams({
					limit: '0',
					where: JSON.stringify({ templatepositionstateid: Number(stateId) }),
				});
				const [photoResp, textResp] = await Promise.all([
					httpClient.get<any[]>(`/api/templatepositionphoto?${params}`),
					httpClient.get<any[]>(`/api/templatepositiontext?${params}`),
				]);
				const photoPositions = photoResp.data.map((p: any) => ({ id: p.id, type: 'photo' }));
				const textPositions = textResp.data.map((p: any) => ({ id: p.id, type: 'text' }));
				positions.value = [...photoPositions, ...textPositions];
			} catch (error: any) {
				console.error('Failed to load positions:', error);
			} finally {
				isLoadingPositions.value = false;
			}
		};

		const saveState = async () => {
			isSaving.value = true;
			try {
				await httpClient.put(`/api/templatestate/${stateId}`, state.value);
				Swal.fire({
					title: 'Saved',
					text: 'Template state has been saved',
					icon: 'success',
					timer: 1500,
				});
			} catch (error: any) {
				Swal.fire({
					title: 'Error',
					text: error.message || 'Failed to save template state',
					icon: 'error',
				});
			} finally {
				isSaving.value = false;
			}
		};

		const editPosition = (position: PositionItem) => {
			router.push({
				path: `/template/${templateId}/state/${stateId}/position/${position.id}`,
				query: { type: position.type },
			});
		};

		onMounted(async () => {
			await Promise.all([loadState(), loadPositions()]);
		});

		return {
			templateId,
			state,
			positions,
			isLoading,
			isLoadingPositions,
			isSaving,
			saveState,
			editPosition,
		};
	},
});
</script>
